import { useTranslation } from "react-i18next";
import { formatDistanceToNow } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import { Check, Circle, Bell, ArrowRight } from "lucide-react";

interface UserNotification {
  id: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  notification: {
    id: string;
    title_it: string;
    title_en: string | null;
    title_es: string | null;
    description_it: string | null;
    description_en: string | null;
    description_es: string | null;
    image_url: string | null;
    created_at: string;
  };
}

interface NotificationItemProps {
  notification: UserNotification;
  onClick: () => void;
  onToggleRead: () => void;
}

export default function NotificationItem({
  notification,
  onClick,
  onToggleRead,
}: NotificationItemProps) {
  const { i18n, t } = useTranslation();

  const localeMap = {
    it: it,
    en: enUS,
    es: es,
  };

  const getTitle = () => {
    switch (i18n.language) {
      case "en":
        return notification.notification.title_en || notification.notification.title_it;
      case "es":
        return notification.notification.title_es || notification.notification.title_it;
      default:
        return notification.notification.title_it;
    }
  };

  const formatRelativeTime = (date: string) => {
    return formatDistanceToNow(new Date(date), {
      addSuffix: true,
      locale: localeMap[i18n.language as keyof typeof localeMap] || it,
    });
  };

  const handleToggleRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleRead();
  };

  return (
    <div
      onClick={onClick}
      className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-surface transition-colors border-b border-border last:border-b-0 ${
        !notification.is_read ? "bg-primary/5" : ""
      }`}
    >
      {/* Immagine 1:1 a sinistra */}
      <div className="w-14 h-14 flex-shrink-0 rounded-md overflow-hidden bg-muted border-2 border-border">
        {notification.notification.image_url ? (
          <img
            src={notification.notification.image_url}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Bell className="w-6 h-6 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Contenuto a destra */}
      <div className="flex-1 min-w-0">
        {/* Titolo + pallino */}
        <div className="font-bold flex items-center gap-2">
          <span className="truncate text-sm">{getTitle()}</span>
          {!notification.is_read && (
            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
          )}
        </div>

        {/* Tempo */}
        <div className="text-xs text-muted-foreground mt-0.5">
          {formatRelativeTime(notification.created_at)}
        </div>

        {/* Azioni: Leggi e Segna come letta/da leggere */}
        <div className="flex items-center gap-3 mt-1.5 text-xs">
          <span className="text-dark font-bold flex items-center gap-1 cursor-pointer hover:opacity-70 transition-opacity">
            {t("notifications.readAction", "Leggi")}
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
          <button
            onClick={handleToggleRead}
            className="flex items-center gap-1 text-dark font-bold hover:opacity-70 transition-opacity"
          >
            {notification.is_read ? (
              <>
                <Circle className="h-3.5 w-3.5" strokeWidth={3} />
                <span>{t("notifications.markAsUnread", "Segna come da leggere")}</span>
              </>
            ) : (
              <>
                <Check className="h-3.5 w-3.5" strokeWidth={3} />
                <span>{t("notifications.markAsRead", "Segna come letta")}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
